/**
 * Profile Status Mapping Utility
 * Maps internal status values to user-friendly display labels
 */

/**
 * Maps internal profile status to display status
 * @param {string} status - Internal status (pending, rejected, approved, disabled)
 * @returns {string} Display status (In Review, Rejected, Active, Paused)
 */
export const mapProfileStatus = (status) => {
  if (!status) return 'In Review';
  
  const statusMap = {
    pending: 'In Review',
    rejected: 'Rejected', 
    approved: 'Active',
    disabled: 'Paused'
  };
  
  return statusMap[status.toLowerCase()] || status;
};

/**
 * Maps display status to CSS class for styling
 * @param {string} displayStatus - Display status (Active, Rejected, In Review, Paused)
 * @returns {string} CSS class name
 */
export const getStatusClass = (displayStatus) => {
  const classMap = {
    'Active': 'text_green',
    'Rejected': 'text_red',
    'In Review': 'text_yellow',
    'Paused': 'text_gray'
  };
  
  return classMap[displayStatus] || 'text_default';
};

/**
 * Maps internal status to CSS-friendly class name
 * @param {string} status - Internal status (pending, rejected, approved, disabled)
 * @returns {string} CSS class name (active, rejected, in-review, paused)
 */
export const getStatusClassName = (status) => {
  if (!status) return 'in-review';
  
  const classMap = {
    pending: 'in-review',
    rejected: 'rejected', 
    approved: 'active',
    disabled: 'paused'
  };
  
  return classMap[status.toLowerCase()] || 'in-review';
};

/**
 * Gets status display with CSS class for React components
 * @param {string} status - Internal status
 * @param {string} className - Additional CSS classes
 * @returns {JSX.Element} Styled status span
 */
export const getStatusDisplay = (status, className = '') => {
  const displayStatus = mapProfileStatus(status);
  const statusClass = getStatusClass(displayStatus);
  
  return (
    <span className={`${statusClass} semibold ${className}`} style={{ textTransform: "capitalize" }}>
      {displayStatus}
    </span>
  );
};

/**
 * Legacy function for backward compatibility
 * Maps admin status and transaction status to display status
 * @param {string} adminStatus - Admin status
 * @param {string} transactionStatus - Transaction status
 * @returns {JSX.Element} Styled status span
 */
export const getLegacyStatus = (adminStatus, transactionStatus) => {
  // Handle legacy status mapping for backward compatibility
  const statusMap = {
    active: 'Active',
    pending: {
      pending: 'Draft',
      completed: 'In Review',
      default: 'In Review'
    },
    completed: {
      pending: 'Make Payment',
      default: 'Completed'
    },
    reviewing: "In Review",
    inactive: 'Paused',
    rejected: 'Rejected',
    disabled: 'Paused'
  };

  const classMap = {
    Active: 'text_green',
    Draft: 'text_yellow',
    Pending: 'text_yellow',
    'Make Payment': 'text_red',
    'In Review': 'text_yellow',
    Rejected: 'text_red',
    Paused: 'text_gray',
    Inactive: 'text_gray',
    Completed: 'text_green'
  };

  let status = statusMap[adminStatus];

  if (typeof status === 'object') {
    status = status[transactionStatus] || status.default;
  }

  const statusClass = classMap[status] || 'text_default';

  return (
    <span className={`${statusClass} semibold`} style={{ textTransform: "capitalize" }}>
      {status}
    </span>
  );
};

/**
 * Checks if profile is disabled/paused
 * @param {string} status - Profile status
 * @returns {boolean} True if profile is disabled/paused
 */
export const isProfileDisabled = (status) => {
  const disabledStatuses = ['disabled', 'paused', 'inactive'];
  return disabledStatuses.includes(status?.toLowerCase());
};

/**
 * Checks if profile is active
 * @param {string} status - Profile status
 * @returns {boolean} True if profile is active
 */
export const isProfileActive = (status) => {
  return status?.toLowerCase() === 'approved' || status?.toLowerCase() === 'active';
};

/**
 * Checks if profile is under review
 * @param {string} status - Profile status
 * @returns {boolean} True if profile is under review
 */
export const isProfileUnderReview = (status) => {
  return status?.toLowerCase() === 'pending';
};

/**
 * Checks if profile is rejected
 * @param {string} status - Profile status
 * @returns {boolean} True if profile is rejected
 */
export const isProfileRejected = (status) => {
  return status?.toLowerCase() === 'rejected';
};
